import React, { useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import type { ColorChangeHandler } from 'react-color';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';

import { FlexBox } from '@eltoro-ui/components';

import TextContent from 'Components/CanvasEditor/components/toolbar/TextContent';
import ImageContent from 'Components/CanvasEditor/components/toolbar/ImageContent';
import ShapeContent from 'Components/CanvasEditor/components/toolbar/ShapeContent';

import ColorPicker from 'Components/CanvasEditor/components/ColorPicker';

import type {
  CanvasElement,
  ICanvasHistory,
  ShapeElementType,
} from 'Components/CanvasEditor/types';
import { SceneManager } from 'Components/CanvasEditor/models/SceneManager';
import CustomIcons from 'assets/icons';
import { Divider } from 'primereact/divider';

const filledElementTypes = ['rect', 'circle', 'text'];
const shapes = ['rect', 'circle', 'ellipse'];

interface ToolbarHeaderProps {
  selectedElement: CanvasElement | null;
  onElementChange: (props: Partial<CanvasElement>, id: string, saveToHistory?: boolean) => void;
  onDeleteElement: (id: string) => void;
  sceneManager: SceneManager | null;
  activeCropper: boolean;
  setActiveCropper: Dispatch<SetStateAction<boolean>>;
  undo: VoidFunction;
  redo: VoidFunction;
  historyStep: number;
  history: ICanvasHistory[];
}

export default function ToolbarHeader({
  selectedElement,
  onElementChange,
  onDeleteElement,
  sceneManager,
  activeCropper,
  setActiveCropper,
  undo,
  redo,
  historyStep,
  history,
}: ToolbarHeaderProps) {
  const onChangeColorPicker: ColorChangeHandler = ({ hex }) => {
    if (!selectedElement) return;

    let props: Partial<CanvasElement> = {};

    if (filledElementTypes.includes(selectedElement?.type)) props = { ...props, fill: hex };
    if (selectedElement.type === 'line') {
      if (selectedElement.closed) props = { ...props, fill: hex };
      props = { ...props, stroke: hex };
    }

    onElementChange(props, selectedElement.id);
  };

  const onDelete = () => {
    if (!selectedElement) return;
    onDeleteElement(selectedElement.id);
  };

  const color = useMemo(() => {
    if (selectedElement) {
      if (filledElementTypes.includes(selectedElement?.type)) return selectedElement.fill as string;
      if (selectedElement.type === 'line') {
        if (selectedElement.closed) return selectedElement.fill;
        return selectedElement.stroke;
      }
      return '';
    }
    return '';
  }, [selectedElement]);

  return (
    <FlexBox alignItems="center" justifyContent="space-between" UNSAFE_className="toolbar-header">
      <FlexBox
        alignItems="center"
        justifyContent="space-between"
        UNSAFE_style={{ width: 'calc(280px - 2rem)' }}
      >
        <h3>Banner Editor</h3>
        <FlexBox alignItems="center" UNSAFE_className="history-toggles-container">
          <button onClick={undo} className="history-toggle" disabled={historyStep <= 0}>
            <CustomIcons
              name="undo"
              fontSize={20}
              color={historyStep <= 0 ? '#D1D1D1' : '#262626'}
            />
          </button>
          <Divider data-pr-classname="history-toggles-divider" layout="vertical" />
          <button
            onClick={redo}
            className="history-toggle"
            disabled={historyStep >= history.length - 1}
          >
            <CustomIcons
              name="redo"
              fontSize={20}
              color={historyStep >= history.length - 1 ? '#D1D1D1' : '#262626'}
            />
          </button>
        </FlexBox>
      </FlexBox>
      <FlexBox
        alignItems="center"
        justifyContent="space-between"
        gap="0.75rem"
        UNSAFE_style={{ flex: 1, paddingInlineStart: 16 }}
      >
        <FlexBox alignItems="center" gap="0.75rem">
          {selectedElement?.fill && (
            <ColorPicker
              type="color"
              selectedElement={selectedElement}
              color={color}
              onChangeColorPicker={onChangeColorPicker}
            />
          )}
          {shapes.includes(selectedElement?.type as string) && (
            <ShapeContent
              selectedElement={selectedElement as ShapeElementType}
              onElementChange={onElementChange}
            />
          )}
          {selectedElement?.type === 'text' && (
            <TextContent selectedElement={selectedElement} onElementChange={onElementChange} />
          )}
          {selectedElement?.type === 'image' && (
            <ImageContent
              selectedElement={selectedElement}
              onElementChange={onElementChange}
              sceneManager={sceneManager}
              activeCropper={activeCropper}
              setActiveCropper={setActiveCropper}
            />
          )}
        </FlexBox>
        {selectedElement && (
          <button className="delete-element" onClick={onDelete}>
            <FontAwesomeIcon icon={faTrash} color="#FF2D38" style={{ fontSize: 16 }} />
          </button>
        )}
      </FlexBox>
    </FlexBox>
  );
}
